import { MoreHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { DropdownMenu, type MenuItem } from '@/components/ui/DropdownMenu';

import { STATUS_ACTIONS } from './statusActions';
import type { StatusActionState } from './useStatusAction';

interface Props {
  state: StatusActionState;
  /** Named in the trigger's accessible name — a card with three runs has three of these (§9). */
  vacancy: string;
  /**
   * The run's date opened, already formatted, and it is in the name for a reason narrower than
   * tidiness: **the vacancy alone does not tell two runs apart in the one case that matters.** A
   * person may hold several considerations at the *same* vacancy — that is exactly the state the 409
   * on these routes exists for (`uniq_active_consideration_per_person_role`, one active run per
   * person and role, closed ones unlimited) — so two triggers would carry one name on precisely the
   * card where a reader has to choose between them. `InterviewRow` puts the date in its *Continue*
   * label for the same reason (§9), and the strip shows the same three facts a sighted reader uses.
   */
  openedDate: string;
}

/**
 * §6.5's *"This is where status changes"* — the status actions of one consideration, as the **items of
 * the run's own overflow menu** (ADR 0010, and §6.5's *The block's composition*). The server's answer
 * to one is `StatusActionAnswer`, rendered beneath the block's strip; the mutation both read is
 * `useStatusAction`.
 *
 * ⚠️ **A menu of acts is not the dropdown ADR 0010 refuses, and this is the file where somebody will
 * assume otherwise.** What that ADR makes unrepresentable is a status set **by value** — a control
 * offering five statuses as choices, with a `status` field in a request body. Every item below is an
 * **act**: it calls one of three endpoints, each of which takes **no body at all**, so what a reader
 * picks is *"record that an offer was made"* and never *"set this row to OFFER"*.
 * `backend/tests/test_status_is_not_writable.py` is the server-side twin, and `statusActions.ts`'s
 * **values** — the affordance table, the three specs, `variant` — are byte-identical across this move; the
 * only edit to that file is a comment saying that "button" in it now means "menu item". **The test is not
 * the shape of the control; it is whether a
 * status can be named as a value anywhere in a request.** It cannot, and that is what must stay true.
 *
 * ⚠️ **Which actions appear is an affordance, not a rule** — `statusActions.ts` holds the set for all
 * five statuses and its docblock gives the reason for each. ADR 0010's table is total; this component
 * enforces nothing and must not be read as if it did.
 *
 * ⚠️ **This menu is not the card header's** (`CardMenu`, *Edit* and *Anonymize*, named *Candidate
 * actions*). Two `⋯` triggers share one card, so this one is named for **its run** — a card with three
 * considerations would otherwise announce three identical menus, which is §9's *"three identical
 * Continue links"* in another place.
 *
 * ⚠️ **The pending action shows in the trigger, not in an item.** A Radix menu closes on select, so
 * there is no item left on screen to carry progress. The trigger takes the loading state and is
 * disabled while a call is in flight, which also stops a second action racing the same row — the
 * server serialises them with a lock rather than merging them.
 */
export function StatusActions({ state, vacancy, openedDate }: Props) {
  const { t } = useTranslation('candidates');
  const { actions, act } = state;

  /**
   * ⚠️ **§5's gate travels with the component, and it used to.** Before TASK-124 split the control from
   * its answer this file held `if (!mayWriteAboutPerson(groups)) return null;` itself; the flag now
   * arrives on the state, and `ConsiderationBlock` checks it too. Both checks stay: `useStatusAction` is
   * the reusable half now, so the next surface that renders a per-run control — §6.8, a compact runs
   * list, §6.2's pipeline — would otherwise be one forgotten wrapper away from offering an `Interviewer`
   * a menu whose every item answers 403 (`security-auditor`, Sprint 17).
   */
  if (!state.mayAct) return null;

  const items: MenuItem[] = actions.map((action) => ({
    label: t(STATUS_ACTIONS[action].labelKey),
    // §4's rule read off the **same field** the button form read, so the two cannot disagree about
    // which act is destructive: `danger` is `negative` text here and an outline there, never a fill.
    destructive: STATUS_ACTIONS[action].variant === 'danger',
    onSelect: () => act.mutate(action),
  }));

  return (
    <DropdownMenu
      trigger={
        <Button
          variant="ghost"
          size="sm"
          // §4 Icons: an icon is never the only label. The accessible name is this one, and the items
          // themselves carry words.
          aria-label={t('card.actions.menu', { vacancy, date: openedDate })}
          isLoading={act.isPending}
          disabled={act.isPending}
        >
          <MoreHorizontal size={16} strokeWidth={1.5} aria-hidden />
        </Button>
      }
      items={items}
    />
  );
}
